import { supabase } from "@/integrations/supabase/client";
import { processCuiInput } from "@/utils/cui-utils";
import { parseSupabaseFunctionError } from "@/utils/supabaseFunctionsError";
import { buildSurseDate } from "@/utils/entitySurse";
import type { EntityData } from "@/types/entity";
import type {
  AnBilant,
  IdentificareFirma,
  RaspunsBilant,
  SurseDate,
  SursaIdentificare,
} from "@/types/identificareFirma";
import { COD_ANAF_NEGASIT } from "@/types/identificareFirma";

export type ClasaEroareIdentificare =
  | "cui_invalid"
  | "negasit"
  | "indisponibil";

export type CompanyDataResult = {
  success: boolean;
  entityData?: Partial<EntityData>;
  anafData?: unknown;
  firma?: IdentificareFirma;
  sursa?: SursaIdentificare;
  la?: string;
  snapshot?: string | null;
  bilant?: RaspunsBilant;
  surseDate?: SurseDate;
  error?: string;
  eroareTip?: ClasaEroareIdentificare;
  notFound?: boolean;
  hasFinancialData?: boolean;
  hasVatRegistration?: boolean;
  anafVatValidated?: boolean;
  originalCuiInput?: string;
  cleanCui?: string;
};

const BILANT_GOL: RaspunsBilant = {
  ani: [],
  an_recomandat: null,
  sursa: "fara_bilant",
};

type RaspunsIdentificare = {
  stare: "gasit";
  firma: IdentificareFirma;
  sursa: SursaIdentificare;
  la: string;
  raw: unknown;
};

export async function clasificaEroareIdentificare(
  error: unknown
): Promise<{ tip: ClasaEroareIdentificare; mesaj: string }> {
  const corp = (await parseSupabaseFunctionError(error)) as {
    error?: string;
    message?: string;
  } | null;
  const status = (error as { context?: { status?: number } } | null)?.context
    ?.status;

  if (corp?.error === COD_ANAF_NEGASIT || status === 404) {
    return {
      tip: "negasit",
      mesaj: corp?.message || "CUI-ul nu a fost găsit în registrul ANAF.",
    };
  }

  return {
    tip: "indisponibil",
    mesaj:
      corp?.message ||
      "Registrul ANAF nu răspunde acum. Încercați din nou sau continuați manual.",
  };
}

async function preiaIdentificare(cui: string): Promise<RaspunsIdentificare> {
  const { data, error } = await supabase.functions.invoke(
    `api/anaf-company?cui=${encodeURIComponent(cui)}`,
    { method: "GET" }
  );

  if (error) {
    const clasificat = await clasificaEroareIdentificare(error);
    const eroare = new Error(clasificat.mesaj);
    eroare.name = clasificat.tip;
    throw eroare;
  }

  if (!data || data.stare !== "gasit") {
    const eroare = new Error(
      "Registrul ANAF nu răspunde acum. Încercați din nou sau continuați manual."
    );
    eroare.name = "indisponibil";
    throw eroare;
  }

  return data as RaspunsIdentificare;
}

async function preiaBilant(cui: string): Promise<RaspunsBilant> {
  const { data, error } = await supabase.functions.invoke(
    `api/financial-data?cui=${encodeURIComponent(cui)}`,
    { method: "GET" }
  );

  if (error || !data || !Array.isArray(data.ani)) {
    return BILANT_GOL;
  }
  return data as RaspunsBilant;
}

export function anBilantAles(bilant: RaspunsBilant): AnBilant | null {
  if (bilant.an_recomandat === null) return null;
  return bilant.ani.find((an) => an.an === bilant.an_recomandat) ?? null;
}

function areDateFinanciare(an: AnBilant | null): boolean {
  if (!an) return false;
  return (
    (an.salariati ?? 0) !== 0 ||
    (an.cifra_afaceri ?? 0) !== 0 ||
    (an.active_totale ?? 0) !== 0
  );
}

function inEuro(valoare: number | null, curs: number | null): number {
  if (!valoare || !curs) return 0;
  return Math.round((valoare / curs) * 100) / 100;
}

export function construiesteEntityData(
  cuiProcesat: {
    originalInput: string;
    cleanCui: string;
    hasVatRegistration: boolean;
  },
  identificare: RaspunsIdentificare,
  bilant: RaspunsBilant,
  surseDate: SurseDate
): Partial<EntityData> {
  const firma = identificare.firma;
  const an = anBilantAles(bilant);
  const curs = an?.curs?.curs ?? null;
  const tvaValidat = identificare.sursa !== "onrc_local" && firma.tva !== null;

  return {
    cui: cuiProcesat.cleanCui,
    original_cui_input: cuiProcesat.originalInput,
    clean_cui: cuiProcesat.cleanCui,
    has_vat_registration: cuiProcesat.hasVatRegistration,
    anaf_vat_validated: tvaValidat,
    anaf_vat_validated_at: tvaValidat ? identificare.la : null,
    nume_entitate: firma.denumire || "",
    caen_principal: {
      cod: firma.caen_principal || "",
      denumire: "",
    },
    caen_secundar: [],
    cif: firma.cod_inmatriculare,
    adresa: firma.adresa || "",
    telefon: firma.telefon || "",
    email: "",
    website: firma.web || "",
    nr_salariati: an?.salariati ?? 0,
    cifra_afaceri: inEuro(an?.cifra_afaceri ?? null, curs),
    active: inEuro(an?.active_totale ?? null, curs),
    original_cifra_afaceri_ron: an?.cifra_afaceri ?? 0,
    original_active_ron: an?.active_totale ?? 0,
    currency_conversion_rate: curs,
    currency_converted_at: curs ? new Date().toISOString() : null,
    anaf_data_fetched_at: identificare.la,
    anaf_last_updated: firma.data_actualizare || "",
    data_source: an ? String(bilant.sursa) : String(identificare.sursa),
    financial_data_year: bilant.an_recomandat,
    surse_date: surseDate,
  } as Partial<EntityData>;
}

export const fetchCombinedCompanyData = async (
  cuiInput: string
): Promise<CompanyDataResult> => {
  const cuiProcesat = processCuiInput(cuiInput);

  if (!cuiProcesat.isValid) {
    return {
      success: false,
      error: cuiProcesat.error || "CUI invalid",
      eroareTip: "cui_invalid",
      originalCuiInput: cuiProcesat.originalInput,
      cleanCui: cuiProcesat.cleanCui,
      hasVatRegistration: cuiProcesat.hasVatRegistration,
    };
  }

  const [rezIdentificare, rezBilant] = await Promise.allSettled([
    preiaIdentificare(cuiProcesat.cleanCui),
    preiaBilant(cuiProcesat.cleanCui),
  ]);

  if (rezIdentificare.status === "rejected") {
    const motiv = rezIdentificare.reason;
    const tip: ClasaEroareIdentificare =
      motiv instanceof Error && motiv.name === "negasit"
        ? "negasit"
        : "indisponibil";
    return {
      success: false,
      eroareTip: tip,
      notFound: tip === "negasit",
      error:
        motiv instanceof Error
          ? motiv.message
          : "Registrul ANAF nu răspunde acum.",
      originalCuiInput: cuiProcesat.originalInput,
      cleanCui: cuiProcesat.cleanCui,
      hasVatRegistration: cuiProcesat.hasVatRegistration,
    };
  }

  const identificare = rezIdentificare.value;
  const bilant =
    rezBilant.status === "fulfilled" ? rezBilant.value : BILANT_GOL;

  const surseDate = buildSurseDate({
    sursa: identificare.sursa,
    la: identificare.la,
    snapshot: identificare.firma.snapshot,
    bilant,
  });

  const entityData = construiesteEntityData(
    cuiProcesat,
    identificare,
    bilant,
    surseDate
  );

  return {
    success: true,
    entityData,
    anafData: identificare.raw,
    firma: identificare.firma,
    sursa: identificare.sursa,
    la: identificare.la,
    snapshot: identificare.firma.snapshot,
    bilant,
    surseDate,
    hasFinancialData: areDateFinanciare(anBilantAles(bilant)),
    hasVatRegistration: cuiProcesat.hasVatRegistration,
    anafVatValidated: entityData.anaf_vat_validated === true,
    originalCuiInput: cuiProcesat.originalInput,
    cleanCui: cuiProcesat.cleanCui,
  };
};

export const formatFinancialAmount = (amount: string | number): string => {
  if (!amount) return "";
  const numStr = typeof amount === "string" ? amount : amount.toString();
  return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

export const validateCUI = (cui: string): boolean => /^\d{7,8}$/.test(cui);

export const extractDomainFromEmail = (email: string): string | null => {
  const match = email.match(/^[^@]+@(.+)$/);
  return match ? match[1] : null;
};

export const fetchIPAddressesFromDomain = async (
  domain: string
): Promise<string[]> => {
  const response = await fetch(
    `https://dns.google/resolve?name=${domain}&type=A`,
    { method: "GET", headers: { Accept: "application/dns-json" } }
  );

  if (!response.ok) {
    throw new Error(
      `DNS lookup failed: ${response.status} ${response.statusText}`
    );
  }

  const data = await response.json();

  if (data.Status !== 0) {
    throw new Error(`DNS resolution failed with status: ${data.Status}`);
  }

  if (data && Array.isArray(data.Answer)) {
    return data.Answer.filter(
      (record: { type: number; data: string }) => record.type === 1
    )
      .map((record: { data: string }) => record.data)
      .filter((ip: string) => Boolean(ip) && typeof ip === "string");
  }

  throw new Error("No A records found for domain");
};
